import React, { useState, useEffect, useRef } from 'react';
import { auth } from '../../lib/firebase';
import { usePath, navigate, parseRoute } from '../../lib/router';
import { usePrograms, type Program, type ServiceProvider } from '../../hooks/usePrograms';
import ProgramConfigModal from './program/ProgramConfigModal';
import { Settings, Plus, X, Save, Trash2, Search, Check, AlertCircle, Building2, FileText, RefreshCw, GraduationCap, ShieldCheck, Loader } from 'lucide-react';
import Modal from '../shared/Modal';
import { useModalLayer } from '../../lib/modalStack';
import { notifyToast } from '../../lib/toastStore';
import { confirmDialog } from '../../lib/confirmStore';
import WorkspaceSettingsSection from '../shared/WorkspaceSettingsSection';
import EmailSenderManager from './EmailSenderManager';

const DEFAULT_DOC_OPTIONS = ['passport', 'ds2019', 'offerLetter', 'resume', 'transcripts', 'bankStatement', 'medicalInsurance', 'identification', 'recommendationLetter', 'englishTestScore', 'other'];

export default function AdminSettings({ profile, initialSubTab }: { profile?: any; initialSubTab?: 'programs' | 'providers' | 'senders' } = {}) {
  const { programs, providers, defaults, loading, refresh } = usePrograms();
  // ADM-08 (decision D4): Email Senders' one home is this sub-tab. The
  // initialSubTab hint (from a retired ?tab=senders deep link) selects it
  // on mount; it is only an initial value — the sub-tab bar works as before.
  const [subTab, setSubTab] = useState<'programs' | 'providers' | 'senders'>(initialSubTab ?? 'programs');
  const [drawer, setDrawer] = useState<{ type: 'program' | 'provider'; edit?: any } | null>(null);
  // Full program configuration workspace (content, documents, pipeline,
  // terms, training and marketing) — the program is the source of truth.
  const [configProgram, setConfigProgram] = useState<any | null>(null);
  const [search, setSearch] = useState('');

  const currentPath = usePath();
  const currentRoute = parseRoute(currentPath);
  const prevConfigProgramRef = useRef<any | null>(null);

  // Sync configProgram with URL path
  useEffect(() => {
    if (currentRoute.type === 'programs_detail') {
      const targetId = currentRoute.id;
      if (configProgram?.id !== targetId && Array.isArray(programs)) {
        const found = programs.find(p => (p.id === targetId || p.programId === targetId));
        if (found) {
          setConfigProgram(found);
          setSubTab('programs');
        }
      }
    } else {
      if (configProgram && currentPath === '/settings') {
        setConfigProgram(null);
      }
    }
  }, [currentPath, currentRoute.type, programs]);

  // Sync URL path when configProgram changes
  useEffect(() => {
    if (configProgram?.id) {
      navigate(`/programs/adminview/${configProgram.id}`);
    } else if (prevConfigProgramRef.current && !configProgram && currentRoute.type === 'programs_detail') {
      navigate('/settings');
    }
    prevConfigProgramRef.current = configProgram;
  }, [configProgram, currentRoute.type]);
  const [isSeeding, setIsSeeding] = useState(false);
  const [backfilling, setBackfilling] = useState(false);

  const showToast = (message: string, type: 'success' | 'error') => {
    notifyToast(type, message);
  };

  const q = search.toLowerCase();
  const filteredPrograms = programs.filter(p => !q || (p.name || '').toLowerCase().includes(q) || (p.code || '').toLowerCase().includes(q) || (p.shortCode || '').toLowerCase().includes(q) || (p.programCode || '').toLowerCase().includes(q) || (p.id || '').toLowerCase().includes(q) || (p.description || '').toLowerCase().includes(q));
  const filteredProviders = providers.filter(p => !q || (p.name || '').toLowerCase().includes(q) || (p.code || '').toLowerCase().includes(q));

  const apiFetch = async (url: string, options?: any) => {
    const token = await auth.currentUser?.getIdToken();
    return fetch(url, { ...options, headers: { ...options?.headers, Authorization: `Bearer ${token}` } });
  };

  const handleSeedPrograms = async () => {
    setIsSeeding(true);
    try {
      const res = await apiFetch('/api/admin/seed-programs', { method: 'POST' });
      if (!res.ok) throw new Error();
      const data = await res.json();
      showToast(`Programs seeded successfully! Updated/created ${data.updatedPrograms || 0} programs.`, 'success');
      // Force clear session storage and refresh
      sessionStorage.removeItem('programsList');
      sessionStorage.removeItem('programsList_at');
      sessionStorage.removeItem('programsList_ver');
      refresh();
    } catch (err) {
      showToast('Failed to seed programs.', 'error');
    } finally {
      setIsSeeding(false);
    }
  };

  const handleBackfillAdminClaims = async () => {
    if (!(await confirmDialog('Backfill the UID-based admin custom claim for all users whose profile/email marks them as admin?\n\nThis repairs accounts that still hit "Missing or insufficient permissions" when saving applicant data. Claims propagate within ~5 min; admins should re-login after this.'))) return;
    setBackfilling(true);
    try {
      const res = await apiFetch('/api/admin/backfill-admin-claims', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ dryRun: false }),
      });
      if (!res.ok) throw new Error();
      const data = await res.json();
      showToast(
        `Backfill complete — scanned ${data.scanned}, already admin ${data.alreadyAdmin}, updated ${data.updated}${data.errors ? `, errors ${data.errors}` : ''}.`,
        data.errors > 0 ? 'error' : 'success',
      );
    } catch {
      showToast('Failed to run admin claim backfill.', 'error');
    } finally {
      setBackfilling(false);
    }
  };

  const handleToggle = async (type: 'program' | 'provider', id: string) => {
    const endpoint = type === 'program' ? `/api/admin/programs/${id}/toggle` : `/api/admin/service-providers/${id}/toggle`;
    try {
      const res = await apiFetch(endpoint, { method: 'PATCH' });
      if (!res.ok) throw new Error();
      showToast(`${type === 'program' ? 'Program' : 'Provider'} updated`, 'success');
    } catch { showToast('Failed to update', 'error'); }
  };

  const handleDelete = async (type: 'program' | 'provider', id: string, name: string) => {
    if (!(await confirmDialog(`Delete ${name}? This action cannot be undone. Existing applications referencing this ${type} will not be affected.`))) return;
    const endpoint = type === 'program' ? `/api/admin/programs/${id}` : `/api/admin/service-providers/${id}`;
    try {
      const res = await apiFetch(endpoint, { method: 'DELETE' });
      if (!res.ok) throw new Error();
      showToast(`${type === 'program' ? 'Program' : 'Provider'} deleted`, 'success');
    } catch { showToast('Failed to delete', 'error'); }
  };

  return (
    <div className="h-full flex flex-col">
      {/* Toasts render via the global root-level notification layer. */}

      {/* Header */}
      <div className="flex items-center justify-between p-4 md:p-6 border-b border-slate-200 bg-white shrink-0 flex-wrap gap-3">
        <div className="flex items-center gap-4">
          <Settings className="h-5 w-5 text-slate-600" />
          <h1 className="text-lg font-bold text-slate-900">Settings</h1>
          <div className="flex gap-1 bg-slate-100 rounded-lg p-1">
            <button onClick={() => setSubTab('programs')} className={`px-3 py-1.5 rounded-md text-xs font-semibold transition cursor-pointer ${subTab === 'programs' ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500 hover:text-slate-700'}`}>Programs</button>
            <button onClick={() => setSubTab('providers')} className={`px-3 py-1.5 rounded-md text-xs font-semibold transition cursor-pointer ${subTab === 'providers' ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500 hover:text-slate-700'}`}>Service Providers</button>
            <button onClick={() => setSubTab('senders')} className={`px-3 py-1.5 rounded-md text-xs font-semibold transition cursor-pointer ${subTab === 'senders' ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500 hover:text-slate-700'}`}>Email Senders</button>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {subTab !== 'senders' && (
            <button onClick={() => setDrawer({ type: subTab === 'programs' ? 'program' : 'provider' })} className="px-3 py-2 btn-primary  text-white rounded-xl text-xs font-semibold transition flex items-center gap-1.5 shadow-sm cursor-pointer">
              <Plus className="h-3.5 w-3.5" /> Add {subTab === 'programs' ? 'Program' : 'Provider'}
            </button>
          )}
        </div>
      </div>

      {/* Workspace (current tenant) — brand config, domains, team */}
      <div className="px-4 md:px-6 pt-4">
        <WorkspaceSettingsSection profile={profile} />
      </div>

      {/* Search */}
      <div className="p-4 md:px-6 border-b border-slate-100">
        <div className="relative max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
          <input type="text" value={search} onChange={e => setSearch(e.target.value)} placeholder={`Search ${subTab}...`} className="w-full pl-10 pr-4 py-2.5 text-sm border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-brand-primary/20  bg-white" />
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto p-4 md:p-6">
        {subTab === 'senders' ? (
          <EmailSenderManager />
        ) : loading ? (
          <div className="space-y-3">{[1,2,3].map(i => <div key={i} className="h-16 bg-slate-100 rounded-xl animate-pulse" />)}</div>
        ) : subTab === 'programs' ? (
          <div className="bg-white rounded-2xl shadow-sm ring-1 ring-slate-100 overflow-hidden">
            {filteredPrograms.length === 0 ? (
              <div className="p-12 text-center"><GraduationCap className="h-10 w-10 text-slate-300 mx-auto mb-3" /><p className="text-sm font-semibold text-slate-700">No programs yet</p><p className="text-xs text-slate-500 mt-1">Add your first program to get started.</p></div>
            ) : (
              <table className="w-full text-sm">
                <thead className="bg-slate-50 text-slate-600 text-xs uppercase font-semibold">
                  <tr>
                    <th className="px-4 py-3 text-left">Name</th>
                    <th className="px-4 py-3 text-left">Code</th>
                    <th className="px-4 py-3 text-left">Visa</th>
                    <th className="px-4 py-3 text-left">Terms</th>
                    <th className="px-4 py-3 text-left">Duration</th>
                    <th className="px-4 py-3 text-left">Status</th>
                    <th className="px-4 py-3 text-right">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {filteredPrograms.map(p => (
                    <tr key={p.id || p.programId} className="hover:bg-slate-50">
                      <td className="px-4 py-3">
                        <button onClick={() => setConfigProgram(p)} className="font-semibold text-slate-900 hover:text-brand cursor-pointer text-left">
                          {p.name}
                        </button>
                      </td>
                      <td className="px-4 py-3 text-slate-500 font-mono text-xs">{p.code}</td>
                      <td className="px-4 py-3">{p.visaType ? <span className="px-1.5 py-0.5 bg-blue-50 text-brand rounded text-[10px] font-bold">{p.visaType}</span> : '—'}</td>
                      <td className="px-4 py-3 text-xs text-slate-600">
                        {p.intakes?.length > 0
                          ? <span className="font-semibold">{p.intakes.filter((t: any) => t.isOpen).length}/{p.intakes.length} open</span>
                          : '—'}
                      </td>
                      <td className="px-4 py-3 text-xs text-slate-600">{p.minDurationWeeks && p.maxDurationWeeks ? `${p.minDurationWeeks}-${p.maxDurationWeeks}w` : '—'}</td>
                      <td className="px-4 py-3">
                        <button onClick={() => handleToggle('program', p.id || p.programId)}
                          className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[10px] font-semibold transition cursor-pointer ${
                            p.active ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'
                          }`}>
                          {p.active ? 'Open' : 'Closed'}
                        </button>
                      </td>
                      <td className="px-4 py-3 text-right">
                        <div className="flex items-center justify-end gap-1">
                          <button onClick={() => setConfigProgram(p)} className="px-2 py-1 text-[11px] font-bold text-brand hover:bg-brand-active rounded-lg transition cursor-pointer" title="Configure documents, pipeline, terms and resources">Configure</button>
                          <button onClick={() => handleDelete('program', p.id || p.programId, p.name)} className="p-1.5 text-slate-400 hover:text-red-600 rounded-lg hover:bg-red-50 transition cursor-pointer" title="Delete"><Trash2 className="h-3.5 w-3.5" /></button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        ) : (
          <div className="bg-white rounded-2xl shadow-sm ring-1 ring-slate-100 overflow-hidden">
            {filteredProviders.length === 0 ? (
              <div className="p-12 text-center"><Building2 className="h-10 w-10 text-slate-300 mx-auto mb-3" /><p className="text-sm font-semibold text-slate-700">No service providers yet</p><p className="text-xs text-slate-500 mt-1">Add your first provider to get started.</p></div>
            ) : (
              <table className="w-full text-sm">
                <thead className="bg-slate-50 text-slate-600 text-xs uppercase font-semibold">
                  <tr><th className="px-4 py-3 text-left">Name</th><th className="px-4 py-3 text-left">Code</th><th className="px-4 py-3 text-left">Country</th><th className="px-4 py-3 text-left">Contact</th><th className="px-4 py-3 text-left">Status</th><th className="px-4 py-3 text-left">Programs</th><th className="px-4 py-3 text-right">Actions</th></tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {filteredProviders.map(p => (
                    <tr key={p.id || p.providerId} className="hover:bg-slate-50">
                      <td className="px-4 py-3"><button onClick={() => setDrawer({ type: 'provider', edit: { ...p, providerId: p.id || p.providerId } })} className="font-semibold text-slate-900 hover:text-brand cursor-pointer text-left">{p.name}</button></td>
                      <td className="px-4 py-3 text-slate-500 font-mono text-xs">{p.code}</td>
                      <td className="px-4 py-3 text-slate-600">{p.country || '—'}</td>
                      <td className="px-4 py-3 text-slate-600 text-xs">{p.contactEmail || '—'}</td>
                      <td className="px-4 py-3">
                        <button onClick={() => handleToggle('provider', p.id || p.providerId)} className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[10px] font-semibold transition cursor-pointer ${p.isActive ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'}`}>
                          {p.isActive ? 'Active' : 'Inactive'}
                        </button>
                      </td>
                      <td className="px-4 py-3"><div className="flex flex-wrap gap-1">{(p.supportedPrograms || []).slice(0, 3).map(sp => <span key={sp} className="px-1.5 py-0.5 bg-brand-active rounded text-[10px] font-semibold">{sp}</span>)}</div></td>
                      <td className="px-4 py-3 text-right">
                        <button aria-label="Delete provider" title="Delete provider" onClick={() => handleDelete('provider', p.id || p.providerId, p.name)} className="p-1.5 text-slate-400 hover:text-red-600 rounded-lg hover:bg-red-50 transition cursor-pointer"><Trash2 className="h-3.5 w-3.5" /></button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        )}
      </div>

      {/* Add/Edit Drawer */}
      {(drawer?.type === 'program' || drawer?.type === 'provider') && (
        <ProgramProviderDrawer
          type={drawer.type}
          edit={drawer.edit}
          programs={programs}
          providers={providers}
          onClose={() => setDrawer(null)}
          onSaved={() => { refresh(); setDrawer(null); showToast(`${drawer.type === 'program' ? 'Program' : 'Provider'} saved`, 'success'); }}
          onDelete={async () => {
            const id = drawer.edit?.programId || drawer.edit?.providerId;
            if (!id || !(await confirmDialog(`Delete ${drawer.edit?.name}? Existing applications referencing this ${drawer.type} will not be affected.`))) return;
            try {
              const token = await auth.currentUser?.getIdToken();
              const endpoint = drawer.type === 'program' ? `/api/admin/programs/${id}` : `/api/admin/service-providers/${id}`;
              const res = await fetch(endpoint, { method: 'DELETE', headers: { Authorization: `Bearer ${token}` } });
              if (!res.ok) throw new Error();
              refresh(); setDrawer(null); showToast('Deleted', 'success');
            } catch { showToast('Failed to delete', 'error'); }
          }}
          showToast={showToast}
        />
      )}

      {/* Program configuration workspace — the single source of truth for
          content, required documents, the program's own master pipeline,
          terms & conditions and training/marketing resources. */}
      {configProgram && (
        <ProgramConfigModal
          program={configProgram}
          allPrograms={programs}
          defaults={defaults}
          onClose={() => setConfigProgram(null)}
          onSaved={() => { refresh(); setConfigProgram(null); }}
          onToast={showToast}
        />
      )}
    </div>
  );
}

function ProgramProviderDrawer({ type, edit, programs, providers, onClose, onSaved, onDelete, showToast }: {
  type: 'program' | 'provider';
  edit?: any;
  programs: Program[];
  providers: ServiceProvider[];
  onClose: () => void;
  onSaved: () => void;
  onDelete?: () => void;
  showToast: (msg: string, t: 'success' | 'error') => void;
}) {
  const isProgram = type === 'program';
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState<any>(() => {
    if (edit) return { ...edit };
    return isProgram
      ? { name: '', code: '', description: '', requirements: '', isOpen: true, visaType: 'J-1', sortOrder: programs.length + 1, terms: [], eligibleCountries: [], requiredDocuments: [], allowedServiceProviders: [], minDurationWeeks: 4, maxDurationWeeks: 52 }
      : { name: '', code: '', contactName: '', contactEmail: '', contactPhone: '', website: '', country: '', isActive: true, supportedPrograms: [], notes: '' };
  });

  const apiFetch = async (url: string, options?: any) => {
    const token = await auth.currentUser?.getIdToken();
    return fetch(url, { ...options, headers: { ...options?.headers, Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' } });
  };

  const handleSave = async () => {
    if (!form.name?.trim()) { showToast('Name is required', 'error'); return; }
    setSaving(true);
    try {
      const entityId = edit?.programId || edit?.providerId || '';
      const endpoint = isProgram
        ? (edit ? `/api/admin/programs/${entityId}` : '/api/admin/programs')
        : (edit ? `/api/admin/service-providers/${entityId}` : '/api/admin/service-providers');
      const method = edit ? 'PUT' : 'POST';
      const body: any = { ...form };
      delete body.id; delete body.programId; delete body.providerId;
      delete body.createdAt; delete body.updatedAt; delete body.createdBy; delete body.deletedAt;
      const res = await apiFetch(endpoint, { method, body: JSON.stringify(body) });
      if (!res.ok) throw new Error('Failed to save');
      onSaved();
    } catch { showToast('Failed to save', 'error'); }
    finally { setSaving(false); }
  };

  const [uploadingMaterial, setUploadingMaterial] = useState<string | null>(null);

  const handleUploadMaterial = async (e: React.ChangeEvent<HTMLInputElement>, materialType: 'training' | 'marketing') => {
    const file = e.target.files?.[0];
    if (!file) return;
    const entityId = edit?.programId || edit?.id || 'temp_prog';

    setUploadingMaterial(materialType);
    try {
      const reader = new FileReader();
      const fileBase64 = await new Promise<string>((resolve, reject) => {
        reader.onload = () => {
          const result = reader.result as string;
          resolve(result.split(',')[1] || result);
        };
        reader.onerror = reject;
        reader.readAsDataURL(file);
      });

      const res = await apiFetch('/api/admin/programs/upload-material', {
        method: 'POST',
        body: JSON.stringify({
          fileName: file.name,
          fileBase64,
          contentType: file.type,
          programId: entityId,
          materialType
        })
      });

      const data = await res.json();
      if (res.ok && data.downloadUrl) {
        const materialField = materialType === 'training' ? 'trainingMaterials' : 'marketingMaterials';
        const currentList = form[materialField] || [];
        const newItem = {
          id: 'mat_' + crypto.randomUUID().replace(/-/g, '').substring(0, 8),
          name: file.name,
          type: 'file',
          url: data.downloadUrl
        };
        set(materialField, [...currentList, newItem]);
        showToast('File uploaded successfully!', 'success');
      } else {
        throw new Error(data.error || 'Upload failed');
      }
    } catch (err: any) {
      console.error(err);
      showToast(err.message || 'Material upload failed', 'error');
    } finally {
      setUploadingMaterial(null);
    }
  };

  const [showTextInput, setShowTextInput] = useState<{ type: 'training' | 'marketing' } | null>(null);
  const textInputZ = useModalLayer(!!showTextInput);
  const [textInputName, setTextInputName] = useState('');
  const [textInputContent, setTextInputContent] = useState('');

  const handleAddTextMaterial = (materialType: 'training' | 'marketing') => {
    setShowTextInput({ type: materialType });
    setTextInputName('');
    setTextInputContent('');
  };

  const confirmTextMaterial = () => {
    if (!showTextInput || !textInputName.trim() || !textInputContent.trim()) return;
    const materialField = showTextInput.type === 'training' ? 'trainingMaterials' : 'marketingMaterials';
    const currentList = form[materialField] || [];
    const newItem = {
      id: 'mat_' + crypto.randomUUID().replace(/-/g, '').substring(0, 8),
      name: textInputName.trim(),
      type: 'text',
      text: textInputContent.trim()
    };
    set(materialField, [...currentList, newItem]);
    setShowTextInput(null);
  };

  const handleRemoveMaterial = (id: string, materialType: 'training' | 'marketing') => {
    const materialField = materialType === 'training' ? 'trainingMaterials' : 'marketingMaterials';
    set(materialField, (form[materialField] || []).filter((m: any) => m.id !== id));
  };

  const set = (field: string, value: any) => setForm((prev: any) => ({ ...prev, [field]: value }));
  const toggleArray = (field: string, item: string) => set(field, (form[field] || []).includes(item) ? form[field].filter((x: string) => x !== item) : [...(form[field] || []), item]);

  return (
    <Modal onClose={onClose} ariaLabel={`${edit ? 'Edit' : 'Add'} ${isProgram ? 'Program' : 'Service Provider'}`}>
      <div className="relative w-full bg-white overflow-y-auto">
        <div className="sticky top-0 bg-white border-b border-slate-200 px-6 py-4 flex items-center justify-between z-10">
          <h2 className="text-base font-bold text-slate-900">{edit ? 'Edit' : 'Add'} {isProgram ? 'Program' : 'Service Provider'}</h2>
          <button aria-label="Close" title="Close" onClick={onClose} className="p-1.5 text-slate-400 hover:text-slate-600 rounded-lg cursor-pointer"><X className="h-5 w-5" /></button>
        </div>

        <div className="p-6 space-y-5">
          {isProgram ? (
            <>
              <div className="space-y-4">
                <h3 className="text-xs font-bold text-slate-500 uppercase tracking-wider">Basic Info</h3>
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1"><label className="text-xs font-semibold text-slate-600">Program Name *</label><input value={form.name} onChange={e => set('name', e.target.value)} className="w-full px-3 py-2 text-sm border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-brand-primary/20" /></div>
                  <div className="space-y-1"><label className="text-xs font-semibold text-slate-600">Short Code *</label><input value={form.code} onChange={e => set('code', e.target.value.toUpperCase())} maxLength={20} disabled={!!edit} className="w-full px-3 py-2 text-sm border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-brand-primary/20 font-mono uppercase disabled:bg-slate-100 disabled:text-slate-500 disabled:cursor-not-allowed" /></div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1"><label className="text-xs font-semibold text-slate-600">Visa Type</label><input value={form.visaType || ''} onChange={e => set('visaType', e.target.value)} className="w-full px-3 py-2 text-sm border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-brand-primary/20" /></div>
                  <div className="space-y-1"><label className="text-xs font-semibold text-slate-600">Sort Order</label><input type="number" value={form.sortOrder || 0} onChange={e => set('sortOrder', parseInt(e.target.value) || 0)} className="w-full px-3 py-2 text-sm border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-brand-primary/20" /></div>
                </div>
                <div className="space-y-1"><label className="text-xs font-semibold text-slate-600">Description</label><textarea value={form.description || ''} onChange={e => set('description', e.target.value)} rows={3} className="w-full px-3 py-2 text-sm border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-brand-primary/20" /></div>
                <div className="space-y-1"><label className="text-xs font-semibold text-slate-600">Requirements</label><textarea value={form.requirements || ''} onChange={e => set('requirements', e.target.value)} rows={3} placeholder="List the qualifications, prerequisites, or conditions for this program..." className="w-full px-3 py-2 text-sm border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-brand-primary/20" /></div>
                <div className="flex items-center gap-2">
                  <button onClick={() => set('isOpen', !form.isOpen)} className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors cursor-pointer ${form.isOpen ? 'bg-green-600' : 'bg-slate-300'}`}>
                    <span className={`inline-block h-3.5 w-3.5 transform rounded-full bg-white transition-transform ${form.isOpen ? 'translate-x-5' : 'translate-x-1'}`} />
                  </button>
                  <span className="text-xs font-semibold text-slate-700">{form.isOpen ? 'Open for Applications' : 'Closed'}</span>
                </div>
              </div>

              <div className="space-y-4">
                <h3 className="text-xs font-bold text-slate-500 uppercase tracking-wider">Duration & Eligibility</h3>
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1"><label className="text-xs font-semibold text-slate-600">Min Duration (weeks)</label><input type="number" value={form.minDurationWeeks || 0} onChange={e => set('minDurationWeeks', parseInt(e.target.value) || 0)} className="w-full px-3 py-2 text-sm border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-brand-primary/20" /></div>
                  <div className="space-y-1"><label className="text-xs font-semibold text-slate-600">Max Duration (weeks)</label><input type="number" value={form.maxDurationWeeks || 0} onChange={e => set('maxDurationWeeks', parseInt(e.target.value) || 0)} className="w-full px-3 py-2 text-sm border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-brand-primary/20" /></div>
                </div>
                <div className="space-y-1"><label className="text-xs font-semibold text-slate-600">Required Documents</label>
                  <div className="flex flex-wrap gap-2">
                    {DEFAULT_DOC_OPTIONS.map(d => <button key={d} type="button" onClick={() => toggleArray('requiredDocuments', d)} className={`px-2.5 py-1 rounded-lg text-[10px] font-semibold transition cursor-pointer ${(form.requiredDocuments || []).includes(d) ? 'btn-primary text-white' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'}`}>{d}</button>)}
                  </div>
                  <div className="flex items-center gap-2 mt-2">
                    <input type="text" id="customDocInput" placeholder="Add custom document..." className="flex-1 px-3 py-1.5 text-xs border border-slate-200 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-brand-primary/20" />
                    <button type="button" onClick={() => { const input = document.getElementById('customDocInput') as HTMLInputElement; if (input?.value?.trim()) { toggleArray('requiredDocuments', input.value.trim()); input.value = ''; } }} className="px-3 py-1.5 text-xs font-bold text-brand hover:bg-blue-50 rounded-lg transition cursor-pointer">Add</button>
                  </div>
                </div>
                <div className="space-y-1"><label className="text-xs font-semibold text-slate-600">Eligible Countries</label><input value={(form.eligibleCountries || []).join(', ')} onChange={e => set('eligibleCountries', e.target.value.split(',').map((s: string) => s.trim()).filter(Boolean))} placeholder="USA, Canada, UK..." className="w-full px-3 py-2 text-sm border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-brand-primary/20" /></div>
              </div>

              <div className="space-y-1">
                <h3 className="text-xs font-bold text-slate-500 uppercase tracking-wider">Allowed Service Providers</h3>
                {providers.length === 0 ? <p className="text-xs text-slate-500">No providers yet. Create providers first.</p> : providers.map(p => (
                  <label key={p.id || p.providerId} className="flex items-center gap-2 py-1.5 cursor-pointer">
                    <input type="checkbox" checked={(form.allowedServiceProviders || []).includes(p.id || p.providerId)} onChange={() => toggleArray('allowedServiceProviders', p.id || p.providerId)} className="rounded border-slate-300 text-brand focus:ring-brand-primary" />
                    <span className="text-sm text-slate-700">{p.name}</span>
                    <span className="text-[11px] text-slate-500 font-mono">{p.code}</span>
                  </label>
                ))}
              </div>

              {/* Training resources */}
              <div className="space-y-3 border-t border-slate-100 pt-4">
                <div className="flex items-center justify-between">
                  <h3 className="text-xs font-bold text-slate-500 uppercase tracking-wider">Training Resources</h3>
                  <div className="flex items-center gap-2">
                    <button type="button" onClick={() => handleAddTextMaterial('training')} className="text-xs text-brand hover:text-brand-dark font-bold cursor-pointer">+ Add Text</button>
                    <label className="text-xs text-brand hover:text-brand-dark font-bold cursor-pointer">
                      + Upload File
                      <input type="file" onChange={(e) => handleUploadMaterial(e, 'training')} className="hidden" />
                    </label>
                  </div>
                </div>
                {uploadingMaterial === 'training' && <p className="text-xs text-slate-500 animate-pulse">Uploading training file...</p>}
                <div className="space-y-2">
                  {(form.trainingMaterials || []).map((m: any) => (
                    <div key={m.id} className="flex items-center justify-between p-2.5 bg-slate-50 border border-slate-200 rounded-xl">
                      <div className="min-w-0 flex-1">
                        <p className="text-xs font-bold text-slate-800 truncate">{m.name}</p>
                        <p className="text-[11px] text-slate-500 capitalize">{m.type}</p>
                      </div>
                      <button aria-label="Remove material" title="Remove material" type="button" onClick={() => handleRemoveMaterial(m.id, 'training')} className="p-1 text-slate-400 hover:text-red-600 rounded cursor-pointer">
                        <X className="h-3.5 w-3.5" />
                      </button>
                    </div>
                  ))}
                  {(form.trainingMaterials || []).length === 0 && <p className="text-xs text-slate-500 italic">No training materials added yet.</p>}
                </div>
              </div>

              {/* Marketing materials */}
              <div className="space-y-3 border-t border-slate-100 pt-4">
                <div className="flex items-center justify-between">
                  <h3 className="text-xs font-bold text-slate-500 uppercase tracking-wider">Marketing Materials</h3>
                  <div className="flex items-center gap-2">
                    <button type="button" onClick={() => handleAddTextMaterial('marketing')} className="text-xs text-brand hover:text-brand-dark font-bold cursor-pointer">+ Add Text</button>
                    <label className="text-xs text-brand hover:text-brand-dark font-bold cursor-pointer">
                      + Upload File
                      <input type="file" onChange={(e) => handleUploadMaterial(e, 'marketing')} className="hidden" />
                    </label>
                  </div>
                </div>
                {uploadingMaterial === 'marketing' && <p className="text-xs text-slate-500 animate-pulse">Uploading marketing file...</p>}
                <div className="space-y-2">
                  {(form.marketingMaterials || []).map((m: any) => (
                    <div key={m.id} className="flex items-center justify-between p-2.5 bg-slate-50 border border-slate-200 rounded-xl">
                      <div className="min-w-0 flex-1">
                        <p className="text-xs font-bold text-slate-800 truncate">{m.name}</p>
                        <p className="text-[11px] text-slate-500 capitalize">{m.type}</p>
                      </div>
                      <button aria-label="Remove material" title="Remove material" type="button" onClick={() => handleRemoveMaterial(m.id, 'marketing')} className="p-1 text-slate-400 hover:text-red-600 rounded cursor-pointer">
                        <X className="h-3.5 w-3.5" />
                      </button>
                    </div>
                  ))}
                  {(form.marketingMaterials || []).length === 0 && <p className="text-xs text-slate-500 italic">No marketing materials added yet.</p>}
                </div>
              </div>
            </>
          ) : (
            <>
              <div className="space-y-4">
                <h3 className="text-xs font-bold text-slate-500 uppercase tracking-wider">Provider Info</h3>
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1"><label className="text-xs font-semibold text-slate-600">Name *</label><input value={form.name} onChange={e => set('name', e.target.value)} className="w-full px-3 py-2 text-sm border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-brand-primary/20" /></div>
                  <div className="space-y-1"><label className="text-xs font-semibold text-slate-600">Short Code *</label><input value={form.code || ''} onChange={e => set('code', e.target.value.toUpperCase())} maxLength={20} disabled={!!edit} className="w-full px-3 py-2 text-sm border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-brand-primary/20 font-mono uppercase disabled:bg-slate-100 disabled:text-slate-500 disabled:cursor-not-allowed" /></div>
                </div>
                <div className="space-y-1"><label className="text-xs font-semibold text-slate-600">Website</label><input type="url" value={form.website || ''} onChange={e => set('website', e.target.value)} placeholder="https://" className="w-full px-3 py-2 text-sm border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-brand-primary/20" /></div>
                <div className="space-y-1"><label className="text-xs font-semibold text-slate-600">Country</label><input value={form.country || ''} onChange={e => set('country', e.target.value)} className="w-full px-3 py-2 text-sm border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-brand-primary/20" /></div>
                <div className="flex items-center gap-2">
                  <button type="button" onClick={() => set('isActive', !form.isActive)} className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors cursor-pointer ${form.isActive ? 'bg-green-600' : 'bg-slate-300'}`}>
                    <span className={`inline-block h-3.5 w-3.5 transform rounded-full bg-white transition-transform ${form.isActive ? 'translate-x-5' : 'translate-x-1'}`} />
                  </button>
                  <span className="text-xs font-semibold text-slate-700">{form.isActive ? 'Active' : 'Inactive'}</span>
                </div>
                <div className="space-y-1"><label className="text-xs font-semibold text-slate-600">Notes</label><textarea value={form.notes || ''} onChange={e => set('notes', e.target.value)} rows={3} className="w-full px-3 py-2 text-sm border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-brand-primary/20" /></div>
              </div>
              <div className="space-y-4">
                <h3 className="text-xs font-bold text-slate-500 uppercase tracking-wider">Contact</h3>
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1"><label className="text-xs font-semibold text-slate-600">Contact Name</label><input value={form.contactName || ''} onChange={e => set('contactName', e.target.value)} className="w-full px-3 py-2 text-sm border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-brand-primary/20" /></div>
                  <div className="space-y-1"><label className="text-xs font-semibold text-slate-600">Contact Email</label><input type="email" value={form.contactEmail || ''} onChange={e => set('contactEmail', e.target.value)} className="w-full px-3 py-2 text-sm border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-brand-primary/20" /></div>
                  <div className="space-y-1"><label className="text-xs font-semibold text-slate-600">Contact Phone</label><input value={form.contactPhone || ''} onChange={e => set('contactPhone', e.target.value)} className="w-full px-3 py-2 text-sm border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-brand-primary/20" /></div>
                </div>
              </div>
              <div className="space-y-1">
                <h3 className="text-xs font-bold text-slate-500 uppercase tracking-wider">Supported Programs</h3>
                {programs.length === 0 ? <p className="text-xs text-slate-500">No programs yet. Create programs first.</p> : programs.map(p => (
                  <label key={p.id || p.programId} className="flex items-center gap-2 py-1.5 cursor-pointer">
                    <input type="checkbox" checked={(form.supportedPrograms || []).includes(p.id || p.programId)} onChange={() => toggleArray('supportedPrograms', p.id || p.programId)} className="rounded border-slate-300 text-brand focus:ring-brand-primary" />
                    <span className="text-sm text-slate-700">{p.name}</span>
                    <span className={`text-[10px] px-1.5 py-0.5 rounded font-semibold ${p.isOpen ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'}`}>{p.isOpen ? 'Open' : 'Closed'}</span>
                  </label>
                ))}
              </div>
            </>
          )}

          {/* Drawer footer */}
          <div className="sticky bottom-0 bg-white border-t border-slate-200 px-6 py-4 -mx-6 -mb-6 mt-6 flex items-center justify-between">
            <div>
              {edit && onDelete && (
                <button type="button" onClick={onDelete} className="text-xs font-semibold text-red-600 hover:text-red-700 cursor-pointer flex items-center gap-1"><Trash2 className="h-3.5 w-3.5" /> Delete</button>
              )}
            </div>
            <div className="flex gap-2">
              <button type="button" onClick={onClose} className="px-4 py-2 text-xs font-semibold text-slate-600 hover:bg-slate-100 rounded-xl transition cursor-pointer">Cancel</button>
              <button type="button" onClick={handleSave} disabled={saving} className="px-4 py-2 btn-primary  disabled:bg-blue-400 text-white rounded-xl text-xs font-semibold transition flex items-center gap-1.5 shadow-sm cursor-pointer">
                <Save className="h-3.5 w-3.5" /> {saving ? 'Saving...' : 'Save'}
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Text material input modal */}
      {showTextInput && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center p-4" style={{ zIndex: textInputZ }} onClick={() => setShowTextInput(null)}>
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
            <div className="px-5 py-4 border-b border-slate-200">
              <h3 className="text-sm font-bold text-slate-900">Add {showTextInput.type === 'training' ? 'Training' : 'Marketing'} Text Resource</h3>
            </div>
            <div className="p-5 space-y-4">
              <div className="space-y-1">
                <label className="text-xs font-semibold text-slate-600">Resource Name *</label>
                <input type="text" value={textInputName} onChange={e => setTextInputName(e.target.value)}
                  placeholder="e.g. Program Overview Guide"
                  className="w-full px-3 py-2 text-sm border border-slate-200 rounded-xl bg-white" />
              </div>
              <div className="space-y-1">
                <label className="text-xs font-semibold text-slate-600">Content / Description *</label>
                <textarea value={textInputContent} onChange={e => setTextInputContent(e.target.value)}
                  placeholder="Enter the content, instructions, or description..."
                  rows={5}
                  className="w-full px-3 py-2 text-sm border border-slate-200 rounded-xl bg-white resize-none" />
              </div>
            </div>
            <div className="flex items-center justify-end gap-2 px-5 py-4 border-t border-slate-200">
              <button onClick={() => setShowTextInput(null)} className="px-4 py-2 text-xs font-semibold text-slate-600 hover:bg-slate-100 rounded-xl cursor-pointer">Cancel</button>
              <button onClick={confirmTextMaterial} disabled={!textInputName.trim() || !textInputContent.trim()}
                className="px-4 py-2 btn-primary disabled:bg-blue-400 text-white rounded-xl text-xs font-semibold cursor-pointer flex items-center gap-1.5">
                <Plus className="h-3.5 w-3.5" /> Add Resource
              </button>
            </div>
          </div>
        </div>
      )}
    </Modal>
  );
}
